// Lista de fusos com rótulo legível e agrupada, compartilhada entre o SPA público do convidado
// (`app/javascript/agenda`) e o dashboard da analista (`app/javascript/dashboard/.../agenda`). Fica em
// `shared/` porque são entrypoints diferentes e a lista não pode ser duplicada: o convidado e a analista
// precisam falar do mesmo fuso.
import BR_CITY_NAMES from './timezoneCityNames.json';

export const timezoneOptions = () =>
  typeof Intl.supportedValuesOf === 'function'
    ? Intl.supportedValuesOf('timeZone')
    : ['America/Sao_Paulo', 'UTC'];

// Os fusos do Brasil, na ordem em que fazem sentido pra quem mora aqui. Ficam num grupo separado no
// topo do seletor: escolher `America/Sao_Paulo` "porque é o do Brasil" estando em Fortaleza desloca
// TODOS os horários em uma hora, e nada depois disso corrige o engano (o email e o .ics saem no fuso
// escolhido; do lado da analista, o engano move as reuniões de todos os links dela).
const BR_TIMEZONES = [
  'America/Sao_Paulo',
  'America/Fortaleza',
  'America/Recife',
  'America/Maceio',
  'America/Bahia',
  'America/Belem',
  'America/Araguaina',
  'America/Santarem',
  'America/Campo_Grande',
  'America/Cuiaba',
  'America/Manaus',
  'America/Boa_Vista',
  'America/Porto_Velho',
  'America/Rio_Branco',
  'America/Eirunepe',
  'America/Noronha',
];

// O IANA não tem acento e chama o fuso oficial de "Sao_Paulo": para brasileiro o nome natural é
// "horário de Brasília", e "Belem"/"Cuiaba" parecem erro de digitação. Só os do Brasil ganham nome
// próprio; os demais seguem com a cidade do identificador. A tabela mora num JSON porque o email do
// convidado (`Agenda::Notifier.tz_label`, no Ruby) lê o mesmo arquivo: tela e email dizem o mesmo nome.
const cityName = timeZone =>
  BR_CITY_NAMES[timeZone] ||
  String(timeZone || '')
    .split('/')
    .pop()
    .replace(/_/g, ' ');

const offsetCache = new Map();

const offsetAt = (timeZone, at) => {
  try {
    return (
      new Intl.DateTimeFormat('en-US', {
        timeZone,
        timeZoneName: 'shortOffset',
      })
        .formatToParts(new Date(at))
        .find(part => part.type === 'timeZoneName')?.value || ''
    );
  } catch {
    return '';
  }
};

// Sem instante, o deslocamento de agora (cacheado: é o que os seletores de fuso listam às centenas). Com
// instante, o daquele momento: fuso estrangeiro com horário de verão muda de GMT entre hoje e a reunião, e
// o email calcula no instante da reunião.
const tzOffset = (timeZone, at) => {
  if (at !== undefined) return offsetAt(timeZone, at);
  if (!offsetCache.has(timeZone))
    offsetCache.set(timeZone, offsetAt(timeZone, Date.now()));
  return offsetCache.get(timeZone);
};

// "America/Fortaleza" -> "Fortaleza (GMT-3)". O identificador IANA cru não confirma nada pra quem não é
// técnico, e é justamente na confirmação que o fuso precisa ser entendido.
export const tzLabel = (timeZone, at) => {
  const city = cityName(timeZone);
  const offset = tzOffset(timeZone, at);
  return offset ? `${city} (${offset})` : city;
};

// Rótulo de fuso para a TELA do candidato (não para o email — lá o GMT é obrigatório). O horário já é
// mostrado no fuso escolhido, que por padrão é o do próprio navegador: nesse caso "no seu horário" diz o
// que "Fortaleza (GMT-3)" tentava dizer, sem o jargão que muita gente não lê.
//
// Compara o RELÓGIO no instante da reunião, não o nome do fuso: São Paulo e Fortaleza marcam a mesma
// hora, e "horário de Fortaleza" para quem está em São Paulo sugeria uma conversão que não existe. O
// instante importa por causa do horário de verão de fusos estrangeiros.
const clockAt = (timeZone, at) =>
  new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(at));

export const tzScreenLabel = (timeZone, browserTimeZone, at = Date.now()) => {
  if (!timeZone || timeZone === browserTimeZone) return 'no seu horário';
  try {
    if (clockAt(timeZone, at) === clockAt(browserTimeZone, at))
      return 'no seu horário';
  } catch {
    // fuso inválido: cai no nome da cidade
  }

  return `horário de ${cityName(timeZone)}`;
};

export const timezoneGroups = (current = '', zones = timezoneOptions()) => {
  const all = current && !zones.includes(current) ? [current, ...zones] : zones;
  const br = BR_TIMEZONES.filter(tz => all.includes(tz));
  const option = tz => ({ value: tz, label: tzLabel(tz) });
  return [
    { label: 'Brasil', zones: br.map(option) },
    {
      label: 'Outros fusos',
      zones: all.filter(tz => !br.includes(tz)).map(option),
    },
  ].filter(group => group.zones.length);
};
